import { useState, useRef } from 'react';
import { setMinutes } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Post } from '@/services/posts';
import { Calendar } from '@/types/calendar';
import { SchedulePicker } from '@/components/composer/SchedulePicker';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { Icon, RetryImage, InfoBox, CustomSelect, Tooltip } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useTimezone } from '@/contexts/TimezoneContext';

/**
 * TimePickerModal Component
 *
 * Modal shown when dropping a post onto the calendar.
 * Allows user to select specific time before mapping post to calendar.
 *
 * Workflow: Drop post -> Show time picker -> Create calendar mapping (MAPPED status)
 */

interface TimePickerModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Close handler
   */
  onClose: () => void;

  /**
   * Post being mapped
   */
  post: Post;

  /**
   * Date the post was dropped on
   */
  selectedDate: Date;

  /**
   * Confirm handler - called with the selected date/time
   */
  onConfirm: (plannedDate: Date, calendarId?: number) => void | Promise<void>;

  /**
   * Whether the mapping mutation is in progress
   */
  isLoading?: boolean;

  /**
   * Available calendars for mapping (shown when multiple)
   */
  calendars?: Calendar[];

  /**
   * Pre-selected calendar ID (defaults to first in list)
   */
  selectedCalendarId?: number;

  /**
   * Variation title (if mapping a variation instead of a post)
   */
  variationTitle?: string;
  /** Compact day placement flow opened from the newly released shelf. */
  fromShelf?: boolean;
}

export const TimePickerModal = ({
  isOpen,
  onClose,
  post,
  selectedDate,
  onConfirm,
  isLoading = false,
  calendars,
  selectedCalendarId,
  variationTitle,
  fromShelf = false,
}: TimePickerModalProps) => {
  const { t } = useTranslation('common');
  const { t: tCal } = useTranslation('calendar');
  const { toUTC } = useTimezone();

  // Initialize with dropped date, preserving the hour from selectedDate
  // (When dropping on a time block, selectedDate already has the target hour set)
  // Convert to UTC immediately so SchedulePicker displays correctly
  const getInitialUTC = () => {
    // Keep the hour from selectedDate (set by drag handler), just zero out minutes
    const localTime = setMinutes(selectedDate, 0);
    return toUTC(localTime);
  };

  const [plannedDateTime, setPlannedDateTime] = useState<Date | null>(() => getInitialUTC());
  const [calendarId, setCalendarId] = useState<number | undefined>(
    selectedCalendarId || calendars?.[0]?.id
  );
  const submitInFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPastTime = plannedDateTime ? plannedDateTime <= new Date() : false;

  // Handle confirm
  const handleConfirm = async () => {
    if (submitInFlightRef.current || isLoading || !plannedDateTime || plannedDateTime <= new Date()) return;
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await onConfirm(plannedDateTime, calendarId);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!isLoading && !submitInFlightRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="time-picker-modal-title"
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className={cn(
              'relative bg-white rounded-lg shadow-xl',
              'w-full max-w-md',
              'transform transition-all',
              'border border-gray-200'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={cn('border-b border-gray-200', fromShelf ? 'px-4 py-3 sm:px-6' : 'px-6 py-4')}>
              <h2
                id="time-picker-modal-title"
                className="text-lg font-semibold text-gray-900"
              >
                {fromShelf
                  ? tCal('release_shelf_choose_day')
                  : variationTitle
                  ? t('map_variation_to_calendar', { defaultValue: 'Map Variation to Calendar' })
                  : t('map_post_to_calendar')}
              </h2>
            </div>

            {/* Content */}
            <div className={cn(fromShelf ? 'space-y-3 px-4 py-3 sm:px-6' : 'px-6 py-4 space-y-4')}>
              {/* Post Preview */}
              <div className={cn('bg-gray-50 rounded-lg border border-gray-200', fromShelf ? 'p-2.5' : 'p-4')}>
                <div className="flex gap-3">
                  {/* Thumbnail if available */}
                  {post.postMedia && post.postMedia.length > 0 && (
                    <div className="flex-shrink-0">
                      <RetryImage
                        src={post.postMedia[0].thumbnailUrl || post.postMedia[0].originalUrl || undefined}
                        alt=""
                        className={cn('rounded object-cover', fromShelf ? 'h-12 w-12' : 'h-16 w-16')}
                      />
                    </div>
                  )}

                  {/* Post info */}
                  <div className="flex-1 min-w-0">
                    {/* Variation badge */}
                    {variationTitle && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700">
                          <Icon name={ICONS.COPY} size="xs" ariaHidden />
                          {variationTitle}
                        </span>
                      </div>
                    )}

                    <p className="text-sm font-medium text-gray-900 line-clamp-2 whitespace-pre-wrap">
                      {post.caption}
                    </p>

                    {/* Platforms */}
                    {!fromShelf && post.postChannels && post.postChannels.length > 0 && (
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {post.postChannels.map((pc) => (
                          <span
                            key={pc.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700"
                          >
                            {pc.channel.platform}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Calendar Picker - only when multiple calendars available */}
              {calendars && calendars.length > 1 && (
                <CustomSelect
                  label={t('select_calendar', { defaultValue: 'Calendar' })}
                  options={calendars.map((cal) => ({
                    value: String(cal.id),
                    label: cal.name,
                  }))}
                  value={calendarId ? String(calendarId) : undefined}
                  onChange={(val) => setCalendarId(Number(val))}
                  disabled={isLoading}
                />
              )}

              {/* Time Picker */}
              <div>
                {!fromShelf && <label className="block text-sm font-medium text-gray-700 mb-2">{t('select_date_time')}</label>}
                <SchedulePicker
                  scheduledTime={plannedDateTime}
                  onScheduleChange={setPlannedDateTime}
                  disabled={isLoading}
                  directMode={true}
                />
              </div>

              {/* Past-time warning */}
              {isPastTime && (
                <InfoBox variant="warning" size="sm">
                  {t('schedule_error_past', { defaultValue: 'The selected time is in the past. Please choose a future time.' })}
                </InfoBox>
              )}

              {/* Info */}
              {fromShelf ? (
                <p className="text-xs leading-4 text-gray-600">{tCal('release_shelf_mapping_note')}</p>
              ) : (
                <InfoBox variant="info" size="sm">{t('mapping_post_info')}</InfoBox>
              )}
            </div>

            {/* Footer */}
            <ModalFooter>
                <Tooltip content={t('cancel')} side="bottom">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading || isSubmitting}
                    className="group flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-lg focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={t('cancel')}
                  >
                    <span className="flex h-[36px] w-[36px] items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-colors group-hover:border-gray-500 group-hover:bg-gray-50 group-focus-visible:border-gray-700">
                      <Icon name={ICONS.CLOSE} size="xs" ariaHidden />
                    </span>
                  </button>
                </Tooltip>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading || isSubmitting || !plannedDateTime || isPastTime}
                  className={cn(
                    'group flex h-[44px] items-center justify-center text-sm font-medium',
                    'focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                >
                  <span className="flex h-[36px] items-center gap-2 rounded-lg border border-transparent bg-primary-600 px-4 text-primary-600-contrast transition-colors group-hover:bg-primary-700 group-focus-visible:border-primary-800">
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>{t('mapping')}</span>
                    </>
                  ) : (
                    <>
                      <Icon name={ICONS.CALENDAR} size="sm" />
                      <span>{fromShelf ? tCal('release_shelf_place') : t('map_to_calendar')}</span>
                    </>
                  )}
                  </span>
                </button>
            </ModalFooter>
          </div>
        </div>
      </div>
    </>
  );
};
